import { useCallback, useEffect, useRef } from 'react';

import { QuickstartCard } from './QuickstartCard';
import { ThemeLogo } from './ThemeLogo';
import {
  QuickstartTheme,
  getResponsiveTextSize,
  getResponsiveSpacing,
  getWishlistButtonImage,
  getResolvedCtaUrl,
} from '../types/theme';
import { getOutboundCtaUrl } from '../utils/steamUrls';
import { useIsLandscape, useForcePortrait } from '../utils/responsiveHooks';

interface QuickstartThankYouRedirectProps {
  theme: QuickstartTheme;
  urlSlug: string;
  hasSlugConfig: boolean;
  autoRedirect?: boolean;
  onRedirect?: (steamUrl: string) => void;
}

/**
 * QuickstartThankYouRedirect Component
 *
 * Displays thank you message with logos and a Steam button image that redirects when clicked.
 * Supports custom layouts with optional social buttons and email capture via boolean flags.
 */
export const QuickstartThankYouRedirect = ({ theme, urlSlug, hasSlugConfig, autoRedirect, onRedirect }: QuickstartThankYouRedirectProps) => {
  const handleCheckItOut = useCallback(() => {
    const ctaUrl = getResolvedCtaUrl(theme);
    if (!ctaUrl) {
      return;
    }

    const finalUrl = getOutboundCtaUrl(ctaUrl, hasSlugConfig);

    // Send analytics callback
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Open in new tab instead of redirecting (prevents canceling analytics request)
    // Works on both mobile and desktop
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }, [onRedirect, theme]);

  const hasAutoRedirected = useRef(false);

  useEffect(() => {
    if (!autoRedirect || hasAutoRedirected.current) {
      return;
    }

    const ctaUrl = getResolvedCtaUrl(theme);
    if (!ctaUrl) {
      return;
    }

    hasAutoRedirected.current = true;

    // Auto-redirect: Use location.href instead of window.open to avoid popup blockers
    // Safari, Opera, and other browsers block window.open() when not triggered by direct user action
    const finalUrl = getOutboundCtaUrl(ctaUrl, hasSlugConfig);

    // Send analytics callback before redirect
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Delay to ensure analytics can be sent (500ms is conservative for network latency)
    // Analytics uses fetch with keepalive:true, which helps during redirects
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [autoRedirect, onRedirect, theme]);

  // Check if using custom CTA layout (any custom text or components enabled)
  const hasCustomLayout = !!(
    theme.ctaHeaderText ||
    theme.ctaSubheaderText ||
    theme.ctaMessageText
  );

  const isLandscape = useIsLandscape();
  const { shouldRotate } = useForcePortrait();

  // Render custom layout if any custom components are configured
  if (hasCustomLayout) {
    return (
      <div
        className='flex items-center justify-center overflow-auto'
        style={
          shouldRotate ? {
            // Landscape mode: force portrait by rotating -90deg
            position: 'fixed',
            left: '50%',
            top: '50%',
            width: '100dvh',
            height: '100dvw',
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
            backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '1rem',
          } : {
            // Portrait mode: normal layout
            minHeight: '100vh',
            backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: isLandscape ? '0.25rem 1rem' : '1rem',
          }
        }
      >
        <QuickstartCard theme={theme} showHeader={false} hideFooter={true}>
          <div className='flex flex-col items-center text-center'>
            {/* Studio Logo */}
            {!theme.hideStudioLogoOnCTA && (
              <ThemeLogo
                kind='studio'
                theme={theme}
                isLandscape={isLandscape}
                className={getResponsiveSpacing('mt', 4, isLandscape)}
              />
            )}

            {/* Custom Header Text */}
            {theme.ctaHeaderText && (
              <div className={getResponsiveSpacing('mt', 8, isLandscape)}>
                <div
                  className={`font-["Inter"] ${getResponsiveTextSize('3xl', isLandscape)} font-bold`}
                  style={{ color: theme.primaryColor }}
                >
                  {theme.ctaHeaderText}
                </div>
              </div>
            )}

            {/* Game Logo */}
            {!theme.hideGameLogoOnCTA && (
              <ThemeLogo
                kind='game'
                theme={theme}
                isLandscape={isLandscape}
                className={theme.ctaHeaderText ? getResponsiveSpacing('mt', 6, isLandscape) : getResponsiveSpacing('mt', 12, isLandscape)}
                loading='eager'
              />
            )}

            {/* ConnectWithUsSection removed - use requireAuth instead for simpler auth flow */}

            {/* Wishlist/Store Button */}
            {getResolvedCtaUrl(theme) && (() => {
              const buttonImage = getWishlistButtonImage(theme);
              return (
                <div className={`${getResponsiveSpacing('mt', 8, isLandscape)} w-full`}>
                  <img
                    src={buttonImage.src}
                    alt={buttonImage.alt}
                    onClick={handleCheckItOut}
                    className='mx-auto cursor-pointer hover:opacity-90'
                    style={{ height: isLandscape ? '48px' : '64px' }}
                  />
                </div>
              );
            })()}

            {/* Powered by Playcast */}
            <ThemeLogo
              kind='poweredBy'
              theme={theme}
              isLandscape={isLandscape}
              className={`${getResponsiveSpacing('mb', 4, isLandscape)} ${getResponsiveSpacing('mt', 12, isLandscape)}`}
            />
          </div>
        </QuickstartCard>
      </div>
    );
  }

  // Default layout: wishlist_only variant
  return (
    <div
      className='flex items-center justify-center overflow-auto'
      style={
        shouldRotate ? {
          // Landscape mode: force portrait by rotating -90deg
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100dvh',
          height: '100dvw',
          transform: 'translate(-50%, -50%) rotate(-90deg)',
          transformOrigin: 'center center',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '1rem',
        } : {
          // Portrait mode: normal layout
          minHeight: '100vh',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: isLandscape ? '0.25rem 1rem' : '1rem',
        }
      }
    >
      <QuickstartCard theme={theme} showHeader={false}>
        <div className='flex flex-col items-center text-center'>
          {/* Studio Logo */}
          <ThemeLogo
            kind='studio'
            theme={theme}
            isLandscape={isLandscape}
            className={getResponsiveSpacing('mt', 4, isLandscape)}
          />

          <div className={getResponsiveSpacing('mt', 16, isLandscape)}>
            <div
              className={`font-["Inter"] ${getResponsiveTextSize('3xl', isLandscape)} font-semibold`}
              style={{ color: theme.primaryColor }}
            >
              WISHLIST NOW!
            </div>
          </div>

          {/* Game Logo */}
          <ThemeLogo
            kind='game'
            theme={theme}
            isLandscape={isLandscape}
            className={getResponsiveSpacing('mt', 8, isLandscape)}
          />

          {getResolvedCtaUrl(theme) && (() => {
            const buttonImage = getWishlistButtonImage(theme);
            return (
            <img
                src={buttonImage.src}
              onClick={handleCheckItOut}
              className={`${getResponsiveSpacing('mt', 8, isLandscape)} ${isLandscape ? 'h-12' : 'h-16'} cursor-pointer transition-opacity hover:opacity-80`}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCheckItOut();
                }
              }}
                alt={buttonImage.alt}
            />
            );
          })()}
        </div>
      </QuickstartCard>
    </div>
  );
};

